// src/components/TopBar.js
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import ProfileMenu from "./ProfileMenu";
import EmailSignup from "./EmailSignup";
import FeedbackForm from "./FeedbackForm";
import LoginModal from "./LoginModal";

/** Truncate breadcrumb text by dropping WHOLE words until it fits. */
function TruncatingBreadcrumb({ text, wrapperRef }) {
  const textRef = React.useRef(null);
  const [display, setDisplay] = React.useState(text || "");

  const recompute = React.useCallback(() => {
    if (!wrapperRef?.current || !text) return;
    const full = String(text);
    const words = full.split(" ");
    let result = full;

    // test element for measuring candidate widths
    const testEl = document.createElement("span");
    testEl.style.visibility = "hidden";
    testEl.style.position = "absolute";
    testEl.style.whiteSpace = "nowrap";
    testEl.style.font = window.getComputedStyle(textRef.current).font;
    document.body.appendChild(testEl);

    for (let i = words.length; i > 0; i--) {
      const candidate = words.slice(0, i).join(" ");
      testEl.textContent = candidate;
      if (testEl.offsetWidth <= wrapperRef.current.clientWidth) {
        result = candidate;
        break;
      }
    }

    document.body.removeChild(testEl);

    // Always keep at least the first part visible
    if (!result && words.length > 0) {
      result = words[0] + " …";
    }
    setDisplay(result);
  }, [text, wrapperRef]);

  React.useEffect(() => {
    recompute();
  }, [recompute]);

  React.useEffect(() => {
    if (!wrapperRef?.current) return;
    const ro = new ResizeObserver(recompute);
    ro.observe(wrapperRef.current);
    window.addEventListener("resize", recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, [wrapperRef, recompute]);

  return (
    <span ref={textRef} className="breadcrumb" style={{ margin: 0 }}>
      {display}
    </span>
  );
}

export default function TopBar({ breadcrumb, breadcrumbStyle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showSignup, setShowSignup] = React.useState(false);
  const [showFeedback, setShowFeedback] = React.useState(false);
  const [showLogin, setShowLogin] = React.useState(false);
  const [user, setUser] = React.useState(null);
  const crumbWrapRef = React.useRef(null);

  React.useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) setUser(JSON.parse(stored));
  }, []);

  const doLogout = () => {
    localStorage.removeItem("user");
    setUser(null);
    window.location.reload();
  };

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        {/* Breadcrumb row */}
        <div
          className="topbar-row"
          style={{
            maxWidth: "1220px",
            width: "90%",
            margin: "24px auto -10px",
            padding: "0 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxSizing: "border-box",
            ...(breadcrumbStyle || {}),
          }}
        >
          {/* Breadcrumb left */}
          <div
            ref={crumbWrapRef}
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            <TruncatingBreadcrumb text={breadcrumb} wrapperRef={crumbWrapRef} />
          </div>

          {/* Profile/Login right */}
          <div style={{ flex: "0 0 auto" }}>
            {user ? (
              <ProfileMenu
                user={user}
                onProfile={() => navigate("/profile")}
                onLogout={doLogout}
              />
            ) : (
              <button className="nav-button" onClick={() => setShowLogin(true)}>
                Login
              </button>
            )}
          </div>
        </div>

        {/* White top nav */}
        <div className="top-nav">
          <div className="nav-left">
            <button className="nav-button back" onClick={() => navigate("/")}>
              <ArrowLeft size={20} strokeWidth={2} /> Back
            </button>

            {user && (
              <button
                className="nav-button nav-button-explore"
                onClick={() => navigate("/personalized")}
              >
                Personalized Treatments
              </button>
            )}
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <button className="nav-button contact" onClick={() => setShowFeedback(true)}>
              Give Feedback
            </button>

            {!user && (
              <button className="nav-button contact" onClick={() => setShowSignup(true)}>
                <Mail size={20} strokeWidth={2} color="white" /> Stay Informed
              </button>
            )}

            {user && location.pathname !== "/about" && (
              <button className="nav-button back" onClick={() => navigate("/about")}>
                About
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showSignup && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close-button" onClick={() => setShowSignup(false)}>
              ×
            </button>
            <EmailSignup />
          </div>
        </div>
      )}

      {showFeedback && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close-button" onClick={() => setShowFeedback(false)}>
              ×
            </button>
            <FeedbackForm />
          </div>
        </div>
      )}

      {showLogin && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onLogin={(userData) => {
            setUser(userData);
            setShowLogin(false);
          }}
        />
      )}
    </>
  );
}
